import { useMutation } from '@apollo/client';

import useLogEvent, { LogLevel } from '../../../helper/useLogEvent';
import { DeleteResult } from '../../../models/MutationResultModels';
import Notification, { NotificationFilterInput } from '../models/Notification';
import { DELETE_NOTIFICATION } from './query';

interface MutationData {
  deleteOneNotification: DeleteResult;
}

interface MutationVariables {
  query: Pick<NotificationFilterInput, '_id'>;
}

/**
 * Delete a notification
 */
function useDeleteNotification() {
  const [execute, { data, error, loading, client }] = useMutation<MutationData, MutationVariables>(DELETE_NOTIFICATION);
  const { logEvent } = useLogEvent();

  const deleteNotification = async (notification: Notification) => {
    const response = await execute({
      variables: {
        query: {
          _id: { _eq: notification._id },
        },
      },
    });

    const isDeleted = !!response.data?.deleteOneNotification.deletedCount;

    // Manually update the Apollo cache
    client.cache.modify({
      fields: {
        notifications: (previous) => {
          return isDeleted ? [...previous].filter((item) => item.__ref !== notification._id) : previous;
        },
      },
    });

    logEvent(LogLevel.Info, 'Delete notification', {
      id: notification._id,
    });

    return isDeleted;
  };

  return { deleteNotification, loading, error, id: data?.deleteOneNotification };
}

export default useDeleteNotification;
